import axios from 'axios';
import * as tus from 'tus-js-client';
import { Session } from '/app/utils/auth';
import { deferredPromise } from '/app/utils/promise';
import { authorization } from '/app/services/middlewares';
import { config } from '/app/config';

const API_BASE_URL = config.api.baseURL;

export default requester => {
    const provider = axios.create();

    return {
        /**
         * Attach file using presigned url
         */
        async attachFile (file, params = {}, { onUpdateProgress = () => {}, cancelToken } = {}) {
            try {
                const payload = { name: file.name, type: file.type, size: file.size, ...params };
                const { data } = await requester.post('/content/attachments/upload', payload);

                if (!data) {
                    throw new Error('No data');
                }

                const { fileKey, signedUrl, id } = data;

                await provider.put(
                    signedUrl,
                    file,
                    {
                        headers: {
                            'Content-Type': file.type,
                            // 'Content-Disposition': `attachment; filename=${file.name}`,
                        },
                        onUploadProgress: (event) => {
                            const progress = Math.round((event.loaded * 100) / event.total);
                            
                            onUpdateProgress(progress);
                        },
                        cancelToken,
                    }
                );

                return { ...payload, fileKey, id };
            } catch (err) {
                throw err;
            }
        },

        /**
         * Attach video using tus protocol
         **/   
        async attachVideo (file, params = {}, { onUpdateProgress = () => {} } = {}) {
            const { resolve, reject, promise } = deferredPromise();

            const query = new URLSearchParams({
                name: file.name,
                type: file.type,
                private: String(Boolean(params.private)),
            });

            const upload = new tus.Upload(file, {
                overridePatchMethod: true,
                endpoint: `${API_BASE_URL}/content/attachments/stream-upload?${query.toString()}`,
                retryDelays: [0, 3000, 5000, 10000, 20000],
                chunkSize: 1024 * 1024 * 100, // 100MB
                metadata: {
                  filename: file.name,
                  filetype: file.type,
                },
                onBeforeRequest: (req) => {
                    const url = req.getURL();

                    if (url.includes('attachments')) {
                        req.setHeader('Authorization', `Bearer ${Session.getToken()}`);
                    }
                },
                onError: reject,
                onProgress: (bytesUploaded, bytesTotal) => {
                  const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);

                  onUpdateProgress(percentage);
                },
                onSuccess: () => {
                    const url = new URL(upload.url);
                    const videoId = url.pathname.split('/').pop();

                    resolve({ videoId, name: file.name, type: file.type });
                },
            });

            // Check if there are any previous uploads to continue.
            upload.findPreviousUploads().then((previousUploads) => {
                // Found previous uploads so we select the first one.
                if (previousUploads.length) {
                    upload.resumeFromPreviousUpload(previousUploads[0])
                }

                // Start the upload
                upload.start()
            }).catch((err) => {
                // Resume lookup hit storage (localStorage may be blocked/private mode).
                // Don't hang the awaited promise — fall back to a fresh upload, and only
                // reject if even that fails to kick off.
                try {
                    upload.start()
                } catch (startErr) {
                    reject(startErr || err)
                }
            })

            return promise;
        },

        /**
         * Get video info
         * 
         * @param {string} videoId
         * @return {object}
         */
        async getVideoInfo (videoId) {
            const { data } = await requester.get(`/content/attachments/video/${videoId}/info`);
            return data;
        },

        /**
         * Get channels
         * 
         * @return {object}
         */
        channels (platformSlug) {
            return requester.get(`/content/channels/${platformSlug}`);
        },

        /**
         * Get channel
         * 
         * @return {object}
         */
        channel (platformSlug, channelSlug) {
            return requester.get(`/content/channels/${platformSlug}/${channelSlug}`);
        },

        /**
         * Get tasks
         * 
         * @return {object}
         */
        tasksQueue (platformSlug, channelSlug, params) {
            return requester.get(`/content/management/${platformSlug}/channels/${channelSlug}/tasks`, { params });
        },

        /**
         * Update task with patch request
         * 
         * @param {number} id
         * @param {object} data
         * @return {object}
         */
        updateTask (platformSlug, channelSlug, data) {
            return requester.patch(`/content/management/${platformSlug}/channels/${channelSlug}/tasks`, data);
        },

        /**
         * Enable video download
         * 
         * @param {string} videoId
         * @return {object}
         */
        enableVideoDownload (videoId, data) {
            return requester.patch(`/content/attachments/video/${videoId}`, data);
        },

        /**
         * Get template data
         * 
         * @param {string} platformSlug
         * @param {string} channelSlug
         * @return {object}
         */
        templateData (platformSlug, channelSlug) {
            return requester.get(`/content/channels/${platformSlug}/${channelSlug}/template`);
        },

        /**
         * Perform a channel member operation (join, leave, etc)
         * 
         * @param {string} platformSlug - Slug of the platform
         * @param {object} payload - Operation details, must include 'channel', 'op', 'data'
         * @return {object}
         */
        channelMemberOperation(platformSlug, payload) {
            return requester.patch(`/content/channels/${platformSlug}/members`, payload);
        },

        /**
         * Poll the video until the API reports it "ready" (i.e. download is
         * enabled), then resolve with the final response.
         *
         * Polls at a steady interval until one of: ready, a terminal failure
         * status, an abort, or the wall-clock safety timeout. Bounded by time
         * rather than a fixed attempt count because MP4 generation scales with
         * video length. Pass `timeoutMs: Infinity` to poll with no time bound.
         *
         * IMPORTANT: readiness is only as correct as `readStatus`. If the API
         * nests the status differently than handled below, the poll never sees
         * "ready" and burns the entire `timeoutMs` before failing.
         *
         * @param {string} videoId
         * @param {object} data - PATCH body (e.g. `{ op: 'enable-download' }`)
         * @param {object} [opts]
         * @param {(update:{step?:string,status?:string,progress?:number})=>void} [opts.onUpdateProgress]
         * @param {AbortSignal} [opts.signal] - abort to stop polling
         * @param {number} [opts.intervalMs=15000] - delay between polls
         * @param {number} [opts.timeoutMs=7200000] - safety bound (2h); Infinity to disable
         * @return {Promise<object>} the "ready" response
         */
        async ensureVideoReady (videoId, data, { onUpdateProgress = () => {}, signal, intervalMs = 15000, timeoutMs = 2 * 60 * 60 * 1000 } = {}) {
            // Read status/step whether the payload is nested (`{ data: {...} }`) or flat.
            const readStatus = (body) => body?.data?.status ?? body?.status;
            const readStep = (body) => body?.data?.step ?? body?.step;
            // Progress may arrive as a Cloudflare-style string ("59.000000").
            const toNum = (v) => {
                const n = typeof v === 'string' ? parseFloat(v) : v;
                return typeof n === 'number' && !Number.isNaN(n) ? n : undefined;
            };
            const readProgress = (body) => toNum(
                body?.data?.progress ?? body?.progress ??
                body?.data?.pctComplete ?? body?.pctComplete ??
                body?.data?.percentComplete ?? body?.percentComplete,
            );
            // Backend-dependent terminal failures — stop early instead of waiting
            // out the whole timeout. Unknown names simply never match (harmless).
            const TERMINAL_FAILURES = ['error', 'failed', 'cancelled', 'canceled'];

            const videoRequester = axios.create({
                baseURL: requester.defaults.baseURL,
                headers: requester.defaults.headers,
            });

            authorization(videoRequester);

            const deadline = Date.now() + timeoutMs;
            const sleep = (ms) => new Promise((resolve, reject) => {
                const timer = setTimeout(resolve, ms);
                signal?.addEventListener('abort', () => {
                    clearTimeout(timer);
                    reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
                }, { once: true });
            });

            let attempt = 0;

            while (true) {
                attempt += 1;

                const response = await videoRequester.patch(`/content/attachments/video/${videoId}`, data, { signal });
                const body = response?.data;
                const status = readStatus(body);
                const step = readStep(body);

                // `download-ready` is terminal; `status === 'ready'` is the legacy fallback.
                if (step === 'download-ready' || status === 'ready') {
                    onUpdateProgress({ step: 'download-ready', progress: 100, status: 'ready' });
                    return response;
                }

                if (TERMINAL_FAILURES.includes(step) || TERMINAL_FAILURES.includes(status)) {
                    const err = new Error(`Video ${videoId} processing failed (step: ${step ?? status})`);
                    err.code = 'VIDEO_PROCESSING_FAILED';
                    err.videoId = videoId;
                    throw err;
                }

                if (Date.now() >= deadline) {
                    const err = new Error(`Video ${videoId} not ready before timeout (step: ${step ?? status ?? 'unknown'})`);
                    err.code = 'VIDEO_NOT_READY';
                    err.videoId = videoId;
                    throw err;
                }

                // Clamp below 100 — only the terminal branch reports 100.
                const progress = readProgress(body);
                onUpdateProgress({
                    step,
                    status,
                    progress: typeof progress === 'number' ? Math.min(99, progress) : undefined,
                });

                await sleep(intervalMs);
            }
        },
    };
};

